import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Icono } from '@xiltrion/ui/icono';
import { Badge } from '@xiltrion/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@xiltrion/ui/tabs';
import { Spinner } from '@xiltrion/ui/spinner';
import { Button } from '@xiltrion/ui/button';
import { Input } from '@xiltrion/ui/input';
import { Label } from '@xiltrion/ui/label';
import { Slider } from '@xiltrion/ui/slider';
import { Switch } from '@xiltrion/ui/switch';
import { Etiquetas, SelectorMultiple, SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { apiPut } from '@/api/client';
import { PageHeader, Card, LoadingState, ErrorState } from '@/components/ui';
import { useSettings, useCaseTypes, useUsers, qk, type Settings } from './api';
import { Field, Help, notify, notifyError } from './shared';
import { useWorkflows } from '@/features/workflows/api';
import { SigningSettingsCards } from './SigningSettings';
import { InboundMailboxCard, useInboundMailbox } from './InboundMailboxCard';
import { MODULE, RADICATION_SERIES_MAX, toRadicationSeries } from '@pqrs/contracts';
import { useSession } from '@/app/session';

function useSetting<K extends keyof Settings>(key: K) {
  const qc = useQueryClient();
  const q = useSettings();
  const [value, setValue] = useState<Settings[K] | null>(null);
  useEffect(() => { if (q.data) setValue(q.data[key]); }, [q.data, key]);
  const save = useMutation({ mutationFn: (v: Settings[K]) => apiPut(`/settings/${key}`, v), onSuccess: () => { qc.invalidateQueries({ queryKey: qk.settings }); qc.invalidateQueries({ queryKey: ['me'] }); notify('success', 'Configuración guardada'); }, onError: notifyError });
  return { q, value, setValue, save };
}

function SettingCard<K extends keyof Settings>({ title, hint, settingKey, children }: { title: string; hint?: string; settingKey: K; children: (v: Settings[K], set: (v: Settings[K]) => void) => React.ReactNode }) {
  const { q, value, setValue, save } = useSetting(settingKey);
  return (
    <Card title={title} actions={<Button size="sm" disabled={!value || save.isPending} onClick={() => value && save.mutate(value)}>{save.isPending ? <Spinner /> : <Icono nombre='mdi:check' />} Guardar</Button>}>
      {hint && <p className="text-sm text-muted-foreground mt-0 mb-3">{hint}</p>}
      {q.isLoading || !value ? <LoadingState rows={2} /> : q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : (
        <div className="flex flex-col gap-3">
          {children(value, setValue)}
        </div>
      )}
    </Card>
  );
}

const radicationPreview = (pattern: string, series: string) => {
  const y = new Date().getFullYear();
  return pattern.replace('{series}', series || 'PQRS').replace('{year}', String(y)).replace('{month}', String(new Date().getMonth() + 1).padStart(2, '0')).replace(/{seq(?::(\d+))?}/, (_, n?: string) => '1'.padStart(Number(n ?? 6), '0'));
};

/** Personas de triaje: quienes revisan las clasificaciones dudosas (parte de la entrada de casos). */
function TriageField({ v, set }: { v: Settings['intake']; set: (v: Settings['intake']) => void }) {
  const users = useUsers();
  return (
    <Field label="Personas de triaje" htmlFor="it-triage" hint="Revisan las clasificaciones dudosas. Sin ellas, esas revisiones quedan sin responsable."><SelectorMultiple id="it-triage" value={v.triageUserIds ?? []} options={(users.data ?? []).filter((u) => u.status === 'ACTIVE' || (v.triageUserIds ?? []).includes(u.id)).map((u) => ({ value: u.id, label: u.displayName }))} buscable placeholder="Elegir personas…" onChange={(ids) => set({ ...v, triageUserIds: ids })} className="w-full" /></Field>
  );
}

/** Solo las personas de triaje (paso de la puesta en marcha); el resto de la entrada de casos queda como está. */
export function TriagePeopleCard() {
  return <SettingCard title="Personas de triaje" settingKey="intake">{(v, set) => <TriageField v={v} set={set} />}</SettingCard>;
}

const CHANNEL_TABS = ['correo', 'comunes'] as const;

export function ChannelsPage() {
  const caseTypes = useCaseTypes();
  const workflows = useWorkflows();
  const { hasModule } = useSession();
  // Sin «Correo entrante» no hay buzón ni reglas que configurar; sin «Portal ciudadano» o sin IA, nada que sugerir en el portal.
  const mailIntake = hasModule(MODULE.mailIntake);
  const suggests = hasModule(MODULE.portal) && hasModule(MODULE.ai);
  const mailbox = useInboundMailbox(mailIntake);
  const [sp, setSp] = useSearchParams();
  const tab = (mailIntake ? CHANNEL_TABS.find((t) => t === sp.get('tab')) : undefined) ?? (mailIntake ? 'correo' : 'comunes');
  const hasMailbox = mailbox.data?.source === 'tenant';
  const wfOptions = (workflows.data ?? []).map((w) => ({ value: w.code, label: w.name }));
  const caseTypeOptions = (caseTypes.data ?? []).map((c) => ({ value: c.code, label: c.name }));
  return (
    <div>
      <PageHeader title="Canales" subtitle="Cómo entran las solicitudes: portal ciudadano, correo, físico y API. El correo tiene su pestaña; la otra reúne lo que aplica a todos los canales: triaje, radicación y duplicados." />
      <Tabs value={tab} onValueChange={(v) => { const n = new URLSearchParams(sp); n.set('tab', v); setSp(n, { replace: true }); }}>
        <TabsList variant="line" className="w-full justify-start flex-wrap gap-y-2 group-data-[orientation=horizontal]/tabs:h-auto">
          {mailIntake && <TabsTrigger value="correo" className="flex-none gap-2"><Icono nombre="mdi:email-outline" /> Correo{mailbox.data && !hasMailbox && <Badge variant="warning-light" size="sm">Sin buzón</Badge>}</TabsTrigger>}
          <TabsTrigger value="comunes" className="flex-none gap-2"><Icono nombre="mdi:tune-variant" /> Configuración de canales</TabsTrigger>
        </TabsList>

        {mailIntake && <TabsContent value="correo">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="xl:col-span-2"><InboundMailboxCard /></div>
            <SettingCard title="Reglas del correo entrante" settingKey="email.inbound" hint="Qué pasa con cada correo que llega al buzón.">
              {(v, set) => (<>
                <div className="flex items-center gap-2"><Switch id="ei-auto" checked={v.autoCreateCase} onCheckedChange={(c) => set({ ...v, autoCreateCase: !!c })} /><Label htmlFor="ei-auto" className="font-normal">Crear caso automáticamente al recibir un correo</Label></div>
                <Field label="Tipo de caso por defecto" htmlFor="ei-type" hint="Provisional: el motor de decisión define el tipo real en el triaje."><SelectorSimple id="ei-type" value={v.defaultCaseTypeCode} options={caseTypeOptions} onChange={(code) => code && set({ ...v, defaultCaseTypeCode: code })} /></Field>
                <Help>El acuse de recibo con el radicado lo envía el flujo del tipo de caso (paso «Notificar ciudadano» con la plantilla «Caso recibido») y va en el mismo hilo del correo del ciudadano. Los correos automáticos y los de la propia entidad no se radican.</Help>
              </>)}
            </SettingCard>
          </div>
        </TabsContent>}

        <TabsContent value="comunes">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <SettingCard title="Entrada de casos" settingKey="intake" hint="Cada caso nuevo se clasifica y luego sigue el flujo de su tipo.">
              {(v, set) => (<>
                <Field label="Flujo general" htmlFor="it-fallback" hint="Para los tipos sin flujo propio."><SelectorSimple id="it-fallback" value={v.fallbackWorkflowCode} options={wfOptions} onChange={(code) => code && set({ ...v, fallbackWorkflowCode: code })} /></Field>
                <TriageField v={v} set={set} />
                <Field label="Tipo provisional" htmlFor="it-type" hint="Con este tipo entra un caso del portal mientras se clasifica."><SelectorSimple id="it-type" value={v.provisionalCaseTypeCode} options={caseTypeOptions} onChange={(code) => code && set({ ...v, provisionalCaseTypeCode: code })} /></Field>
                {suggests && <>
                  <div className="flex items-center gap-2"><Switch id="it-suggest" checked={v.portalTypeSuggestion !== false} onCheckedChange={(c) => set({ ...v, portalTypeSuggestion: !!c })} /><Label htmlFor="it-suggest" className="font-normal">Sugerir el tipo en el portal</Label></div>
                  <Help>El portal le propone al ciudadano el tipo de su solicitud y él lo confirma o elige otro.</Help>
                </>}
              </>)}
            </SettingCard>
            <SettingCard title="Formato de radicación" settingKey="radication.format" hint="Número único que identifica cada caso, sea cual sea su canal. Nunca se repite ni se reutiliza.">
              {(v, set) => (<>
                <Field label="Serie" htmlFor="rf-series" hint="Mayúsculas y números."><Input id="rf-series" value={v.series} maxLength={RADICATION_SERIES_MAX} onChange={(e) => set({ ...v, series: toRadicationSeries(e.target.value) })} /></Field>
                <Field label="Patrón" htmlFor="rf-pattern" hint="Variables: {series}, {year}, {month}, {seq:N} (N dígitos)."><Input id="rf-pattern" value={v.pattern} onChange={(e) => set({ ...v, pattern: e.target.value })} /></Field>
                <div className="text-sm">Vista previa: <code className="font-sans bg-muted px-2 py-0.5 rounded">{radicationPreview(v.pattern, v.series)}</code></div>
              </>)}
            </SettingCard>
            <SettingCard title="Detección de duplicados" settingKey="duplicates.threshold" hint="Casos con similitud superior al umbral se marcan como posibles duplicados para revisión.">
              {(v, set) => (<>
                <Field label={`Umbral de similitud: ${Math.round(v.similarity * 100)}%`} htmlFor="dup-th">
                  <Slider id="dup-th" value={[Math.round(v.similarity * 100)]} min={50} max={100} onValueChange={([n]) => set({ similarity: (n ?? 50) / 100 })} aria-label="Umbral de similitud" className="my-2" />
                </Field>
                <Help>{v.similarity >= 0.9 ? 'Muy estricto: solo textos casi idénticos.' : v.similarity >= 0.75 ? 'Equilibrado: recomendado para la mayoría de entidades.' : 'Permisivo: marcará más casos, con más falsos positivos.'}</Help>
              </>)}
            </SettingCard>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function NotificationsPage() {
  return (
    <div>
      <PageHeader title="Notificaciones" subtitle="Canales por los que se avisa a ciudadanos y funcionarios, y atajos de teclado." />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SettingCard title="Canales por defecto" settingKey="notifications.defaults" hint="Aplican cuando el ciudadano no ha indicado preferencia.">
          {(v, set) => (<>
            <div className="flex items-center gap-2"><Switch id="nd-email" checked={v.email} onCheckedChange={(c) => set({ ...v, email: !!c })} /><Label htmlFor="nd-email" className="font-normal">Correo electrónico</Label></div>
            <div className="flex items-center gap-2"><Switch id="nd-portal" checked={v.portal} onCheckedChange={(c) => set({ ...v, portal: !!c })} /><Label htmlFor="nd-portal" className="font-normal">Portal ciudadano</Label></div>
            <div className="flex items-center gap-2"><Switch id="nd-sms" checked={v.sms} onCheckedChange={(c) => set({ ...v, sms: !!c })} /><Label htmlFor="nd-sms" className="font-normal">SMS</Label></div>
          </>)}
        </SettingCard>
        <SettingCard title="Atajos de teclado" settingKey="ui.shortcuts" hint="Los usuarios también pueden desactivarlos en su perfil.">
          {(v, set) => <div className="flex items-center gap-2"><Switch id="sc-enabled" checked={v.enabled} onCheckedChange={(c) => set({ enabled: !!c })} /><Label htmlFor="sc-enabled" className="font-normal">Atajos habilitados por defecto</Label></div>}
        </SettingCard>
      </div>
    </div>
  );
}

/** Expediente electrónico: índice firmado (Acuerdo 001 de 2024 AGN), tipologías, retención, firma y TSA de la entidad. */
export function ArchiveSettingsPage() {
  return (
    <div>
      <PageHeader title="Expediente electrónico" subtitle="Cada documento entra al índice electrónico del expediente con su número y su huella SHA-256; al cierre el índice se firma digitalmente y los archivos quedan en almacenamiento inmutable." />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SettingCard title="Índice y tipologías" settingKey="archive" hint="La entidad productora y las tipologías aparecen en el índice; la retención aplica a los tipos de caso que no fijan la suya.">
          {(v, set) => (<>
            <Field label="Entidad productora" htmlFor="ar-entity" hint="Vacío: el nombre de la entidad (Configuración › Entidad)."><Input id="ar-entity" value={v.entityName ?? ''} maxLength={200} onChange={(e) => set({ ...v, entityName: e.target.value || undefined })} /></Field>
            <Field label="Tipologías documentales" htmlFor="ar-types" hint="Se ofrecen al adjuntar. Enter o coma para agregar."><Etiquetas id="ar-types" value={v.documentTypes} onChange={(documentTypes) => set({ ...v, documentTypes })} validar={(t) => t.trim().length > 0 && t.length <= 80} /></Field>
            <Field label="Retención por defecto (años)" htmlFor="ar-ret" hint="Contados desde el cierre del expediente."><Input id="ar-ret" type="number" inputMode="numeric" min={1} max={100} className="tnum" value={v.retentionYears} onChange={(e) => set({ ...v, retentionYears: Number(e.target.value) || 1 })} /></Field>
          </>)}
        </SettingCard>
        <SigningSettingsCards />
      </div>
    </div>
  );
}
